// Jerseys drawn from a leg's team colors (#572): a sport silhouette, a kit
// pattern from a small vocabulary and an optional number, as a compact SVG.
// No remote image and no licensed artwork. The same team gives the same
// bytes, so the widget's frame patcher keeps the <img> across refreshes.
// Every input is validated here: the team arrives from the API but the
// renderer trusts nothing it interpolates.

import teamMarks from '../teammarks/teamMarks.json'

const LEADING_HASH_PATTERN = /^#/
const LEADING_NUMBER_ZEROES_PATTERN = /^0+(?=[1-9])/
const WHITESPACE_OR_HYPHEN_RUN_PATTERN = /[\s-]+/g
const URI_COMPONENT_PUNCTUATION_PATTERN = /[!'()*]/g

const HEX = /^#[0-9a-f]{6}$/i
const NUMBER = /^\d{1,2}$/

// Every league the catalog covers numbers its shirts 0-99 (00 in the NBA,
// WNBA and MLB), and the chest has room for two digits only.
export const MAX_PLAYER_NUMBER_DIGITS = 2

/**
 * A player's shirt number as it is stored and drawn, the same rule as Go's
 * models.NormalizePrepackPlayerNumber: trimmed, one leading "#" dropped, zeros
 * before another digit dropped ("007" is "7", while "0" and "00" stay), and
 * '' unless one or two digits remain.
 */
export function normalizePlayerNumber(value) {
  const number = String(value ?? '').trim().replace(LEADING_HASH_PATTERN, '').trim().replace(LEADING_NUMBER_ZEROES_PATTERN, '')
  return NUMBER.test(number) ? number : ''
}

// The kit pattern vocabulary, shared with Go (package teammarks), which
// stores only these.
export const JERSEY_PATTERNS = Object.freeze([...teamMarks.kit_patterns])

/** The sport key vocabulary of `sportGlyph`: lower case, spaces and dashes as underscores. */
export function normalizeSportKey(sport) {
  return String(sport ?? '').trim().toLowerCase().replace(WHITESPACE_OR_HYPHEN_RUN_PATTERN, '_')
}

const hex = (value) => (typeof value === 'string' && HEX.test(value.trim()) ? value.trim().toLowerCase() : '')

function toRgb(color) {
  return [1, 3, 5].map((start) => Number.parseInt(color.slice(start, start + 2), 16))
}

function mix(from, to, share) {
  const a = toRgb(from)
  const b = toRgb(to)
  return `#${a.map((channel, index) => Math.round(channel + (b[index] - channel) * share).toString(16).padStart(2, '0')).join('')}`
}

function luminance(color) {
  const [r, g, b] = toRgb(color).map((channel) => {
    const value = channel / 255
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function contrast(a, b) {
  const [lighter, darker] = [luminance(a), luminance(b)].sort((x, y) => y - x)
  return (lighter + 0.05) / (darker + 0.05)
}

const readableOn = (background) => (contrast('#ffffff', background) >= contrast('#111111', background) ? '#ffffff' : '#111111')

// A trim below this contrast with the body is invisible at 24px and gets derived instead.
const MIN_TRIM_CONTRAST = 1.25
// A number color below this contrast with the body is replaced by white or near-black.
const MIN_NUMBER_CONTRAST = 2
// Bodies lighter than this get a darker derived trim; darker bodies get a lighter one.
const LIGHT_BODY_LUMINANCE = 0.35
const DERIVED_TRIM_TOWARD_BLACK = 0.45
const DERIVED_TRIM_TOWARD_WHITE = 0.55

// Silhouettes on a 40×40 grid. `cuffs` are the left sleeve's cuff corners
// (outer, inner) and where the sleeve meets the shoulder and armpit, for the
// sleeve stripes; the right sleeve mirrors them. `sides` are where the sleeves
// end and the body starts.
const SHAPES = {
  tee: {
    path: 'M15 5L8 7.5L2 14L6.5 19L10 16.5V36Q10 37 11 37H29Q30 37 30 36V16.5L33.5 19L38 14L32 7.5L25 5Q20 10.5 15 5Z',
    collar: 'M15 5Q20 10.5 25 5',
    sleeve: [[2, 14], [6.5, 19], [8, 7.5], [10, 16.5]],
    sides: [10, 30],
  },
  tank: {
    path: 'M14 4L10 5Q11 13 7 16.5V36Q7 37 8 37H32Q33 37 33 36V16.5Q29 13 30 5L26 4Q20 14 14 4Z',
    collar: 'M14 4Q20 14 26 4M10 5Q11 13 7 16.5M30 5Q29 13 33 16.5',
    sleeve: null,
    sides: [10, 30],
  },
  sweater: {
    path: 'M15 4.5L8 6L2.5 12L1 26.5L6 27.5L8.5 18V35.5Q8.5 37 10 37H30Q31.5 37 31.5 35.5V18L34 27.5L39 26.5L37.5 12L32 6L25 4.5Q20 9.5 15 4.5Z',
    collar: 'M15 4.5Q20 9.5 25 4.5',
    sleeve: [[1, 26.5], [6, 27.5], [2.5, 12], [8.5, 18]],
    sides: [8.5, 31.5],
  },
  button: {
    path: 'M15 5L8 7.5L3 13L7 17.5L10 15.5V36Q10 37 11 37H29Q30 37 30 36V15.5L33 17.5L37 13L32 7.5L25 5L20 11.5Z',
    collar: 'M15 5L20 11.5L25 5',
    sleeve: [[3, 13], [7, 17.5], [8, 7.5], [10, 15.5]],
    sides: [10, 30],
    placket: 'M20 11.5V37',
  },
}

// Font size by digit count, and the baseline that centres the digits on the chest just below the collar.
const NUMBER_SIZES = { 1: 10, 2: 9 }
const NUMBER_BASELINE = 24

const SHAPE_BY_SPORT = { basketball: 'tank', hockey: 'sweater', ice_hockey: 'sweater', baseball: 'button', softball: 'button' }

/** The silhouette a sport wears: tank, sweater, button-front or (default) tee. */
export function jerseyShape(sport) {
  return SHAPE_BY_SPORT[normalizeSportKey(sport)] || 'tee'
}

const round = (value) => Math.round(value * 100) / 100
const mirror = (points) => points.map(([x, y]) => [40 - x, y])
const lerp = ([ax, ay], [bx, by], share) => [round(ax + (bx - ax) * share), round(ay + (by - ay) * share)]

// Vertical bands centred on the middle of the shirt, every `step`.
function bands(trim, width, step) {
  const rects = []
  for (let center = 20 - step * Math.floor(20 / step); center <= 40; center += step) {
    rects.push(`<rect x='${round(center - width / 2)}' y='0' width='${width}' height='40' fill='${trim}'/>`)
  }
  return rects.join('')
}

function sleeveStripes(shape, trim) {
  if (!shape.sleeve) return ''
  const lines = []
  for (const sleeve of [shape.sleeve, mirror(shape.sleeve)]) {
    const [outer, inner, shoulder, armpit] = sleeve
    for (const share of [0.16, 0.34]) {
      const [x1, y1] = lerp(outer, shoulder, share)
      const [x2, y2] = lerp(inner, armpit, share)
      lines.push(`<line x1='${x1}' y1='${y1}' x2='${x2}' y2='${y2}' stroke='${trim}' stroke-width='1.6'/>`)
    }
  }
  return lines.join('')
}

function patternLayer(pattern, shape, trim) {
  const [left, right] = shape.sides
  switch (pattern) {
    case 'stripes': return bands(trim, 4, 8)
    case 'thin-stripes': return bands(trim, 1.5, 4)
    case 'pinstripes': return bands(trim, 0.6, 3)
    case 'hoops': return [11, 19, 27].map((y) => `<rect x='0' y='${y}' width='40' height='4' fill='${trim}'/>`).join('')
    case 'sash': return `<path d='M5 4H12L37 37H30Z' fill='${trim}'/>`
    case 'halves': return `<rect x='20' y='0' width='20' height='40' fill='${trim}'/>`
    case 'contrast-sleeves': return `<rect x='0' y='0' width='${left}' height='40' fill='${trim}'/><rect x='${right}' y='0' width='${40 - right}' height='40' fill='${trim}'/>`
    case 'sleeve-stripes': return sleeveStripes(shape, trim)
    case 'shoulder-yoke': return `<path d='M0 0H40V11Q20 15.5 0 11Z' fill='${trim}'/>`
    case 'hem-stripe': return `<rect x='0' y='29' width='40' height='3' fill='${trim}'/><rect x='0' y='33.5' width='40' height='1.2' fill='${trim}'/>`
    default: return ''
  }
}

/** The team's kit after validation, or null when there is no usable body color. */
function kitFor(team) {
  if (!team || typeof team !== 'object') return null
  const kit = team.kit && typeof team.kit === 'object' ? team.kit : {}
  const colors = Array.isArray(team.colors) ? team.colors : []
  const body = hex(kit.body) || hex(colors[0])
  if (!body) return null
  let trim = hex(kit.trim) || hex(colors[1])
  // A trim the eye cannot tell from the body draws nothing: derive one.
  if (!trim || contrast(trim, body) < MIN_TRIM_CONTRAST) {
    trim = luminance(body) > LIGHT_BODY_LUMINANCE ? mix(body, '#000000', DERIVED_TRIM_TOWARD_BLACK) : mix(body, '#ffffff', DERIVED_TRIM_TOWARD_WHITE)
  }
  const pattern = JERSEY_PATTERNS.includes(kit.pattern) ? kit.pattern : 'solid'
  const number = typeof team.number === 'string' && NUMBER.test(team.number) ? team.number : ''
  const wanted = hex(kit.number_color)
  const numberColor = wanted && contrast(wanted, body) >= MIN_NUMBER_CONTRAST ? wanted : readableOn(body)
  return { body, trim, pattern, number, numberColor }
}

/**
 * The jersey as SVG source, or '' when the team has no usable colors.
 * Attributes use single quotes and the source carries no `;`, `&` or `#`
 * beyond the validated hex colors.
 */
export function jerseySvg(team, sport) {
  const kit = kitFor(team)
  if (!kit) return ''
  const shape = SHAPES[jerseyShape(sport)]
  const { body, trim, pattern, number, numberColor } = kit
  // One bold digit is about 0.56em wide: at 10 it spans roughly 40-45% of the
  // 20-unit torso with its halo; two digits step down to fit.
  const size = NUMBER_SIZES[number.length] || 0
  const parts = [
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40' width='40' height='40'>`,
    `<defs><clipPath id='j'><path d='${shape.path}'/></clipPath></defs>`,
    "<g clip-path='url(#j)'>",
    `<path d='${shape.path}' fill='${body}'/>`,
    patternLayer(pattern, shape, trim),
    `<path d='${shape.collar}' fill='none' stroke='${trim}' stroke-width='3'/>`,
    shape.placket ? `<path d='${shape.placket}' fill='none' stroke='${trim}' stroke-width='1.2'/>` : '',
    '</g>',
    // A neutral, half-transparent edge: a white kit still shows on a light card, a black one on a dark card.
    `<path d='${shape.path}' fill='none' stroke='#808080' stroke-opacity='.75' stroke-width='1.2' stroke-linejoin='round'/>`,
    number
      ? `<text x='20' y='${NUMBER_BASELINE}' text-anchor='middle' font-family='Arial,Helvetica,sans-serif' font-size='${size}' font-weight='800' fill='${numberColor}' stroke='${body}' stroke-width='1.4' stroke-linejoin='round' paint-order='stroke'>${number}</text>`
      : '',
    '</svg>',
  ]
  return parts.join('')
}

// encodeURIComponent leaves ! ' ( ) * as they are; encode them too, so the
// URI is inert inside any HTML attribute and escapeHtml leaves it unchanged.
const encode = (value) => encodeURIComponent(value).replace(URI_COMPONENT_PUNCTUATION_PATTERN, (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`)

/** `data:image/svg+xml,` + the percent-encoded SVG source. The value never contains `;`, `#` or a quote. */
export function svgDataUri(svg) {
  return `data:image/svg+xml,${encode(svg)}`
}

/** The jersey as a data URI (see svgDataUri), or '' when the team has no usable colors. */
export function jerseyDataUri(team, sport) {
  const svg = jerseySvg(team, sport)
  return svg ? svgDataUri(svg) : ''
}
